import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";
import { 
  Calendar, Plane, AlertTriangle, CheckCircle2, Clock, 
  FileText, MapPin, User, ChevronRight, RefreshCw,
  XCircle, AlertOctagon, ClipboardList, PlayCircle, ArrowRight,
  Target, Shield, Award
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

// Live Clock Component for operational dispatch feel
const LiveClock = ({ locale }) => {
  const [time, setTime] = useState(new Date());
  
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white rounded-lg" data-testid="live-clock">
      <Clock className="h-4 w-4 text-emerald-400" />
      <span className="font-mono text-sm tabular-nums">
        {time.toLocaleTimeString(locale || 'cs-CZ', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </span>
    </div>
  );
};

export const DailyOpsDashboard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [pilots, setPilots] = useState({});
  const [aircraft, setAircraft] = useState({});
  const [insuranceStatus, setInsuranceStatus] = useState(null);
  const [authorizationStatus, setAuthorizationStatus] = useState(null);
  const [scenarioStats, setScenarioStats] = useState(null);

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      
      const [summaryRes, pilotsRes, aircraftRes, insuranceRes, authorizationRes, scenarioStatsRes] = await Promise.all([
        api.get('/missions/daily-ops/summary'),
        api.get('/pilots'),
        api.get('/aircraft'),
        api.get('/insurance/status').catch(() => ({ data: null })),
        api.get('/authorizations/status').catch(() => ({ data: null })),
        api.get('/scenarios/stats').catch(() => ({ data: null }))
      ]);
      
      setData(summaryRes.data);
      setInsuranceStatus(insuranceRes.data);
      setAuthorizationStatus(authorizationRes.data);
      setScenarioStats(scenarioStatsRes.data);
      
      const pilotsMap = {};
      pilotsRes.data.forEach(p => { pilotsMap[p.id] = p; });
      setPilots(pilotsMap);
      
      const aircraftMap = {};
      aircraftRes.data.forEach(a => { aircraftMap[a.id] = a; });
      setAircraft(aircraftMap);
      
    } catch (error) {
      toast.error(t.messages?.fetchError || "Failed to fetch data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t.messages?.fetchError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString(t.lang === 'cs' ? 'cs-CZ' : 'en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Calculate key metrics
  const needsAttention = (data?.summary?.draft || 0) + (data?.compliance_issues || 0);
  const readyToFly = (data?.summary?.ready || 0) + (data?.summary?.active || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin" style={{ color: 'var(--text-muted)' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="daily-ops-dashboard">
      {/* Header with Date and Live Clock - Operational Dispatch Style */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b-2 border-slate-200">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-bold truncate text-[var(--text-primary)]">
              {t.dailyOps?.title || "Daily Ops Dashboard"}
            </h1>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 text-xs flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" style={{ animationDuration: '2s' }}></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              {t.dashboard?.live || "LIVE"}
            </Badge>
          </div>
          <div className="flex items-center gap-4 mt-2 flex-wrap">
            <p className="font-medium text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }}>
              {formatDate(data?.date)}
            </p>
            <LiveClock locale={t.locale} />
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="gap-2 flex-shrink-0 self-start sm:self-auto"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          {t.actions?.refresh || "Refresh"}
        </Button>
      </div>

      {/* Primary Status Cards - With Colored Left Borders for urgency */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4">
        {/* Needs Attention - Red/Green border based on urgency */}
        <Card className={`border-l-4 ${needsAttention > 0 ? "border-l-rose-500 bg-rose-50/50" : "border-l-emerald-500 bg-emerald-50/50"}`}>
          <CardContent className="p-4 lg:pt-6 lg:p-6">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className={`text-xs lg:text-sm font-medium ${needsAttention > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {t.dailyOps?.needsAttention || "Needs Attention"}
                </p>
                <p className={`text-3xl lg:text-4xl font-bold ${needsAttention > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                  {needsAttention}
                </p>
                <p className={`text-xs mt-0.5 lg:mt-1 truncate opacity-70 ${needsAttention > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {needsAttention > 0 
                    ? (t.dailyOps?.missionsNeedWork || "missions need work")
                    : (t.dailyOps?.allClear || "all clear")}
                </p>
              </div>
              {needsAttention > 0 ? (
                <AlertTriangle className="h-8 w-8 lg:h-12 lg:w-12 flex-shrink-0 text-rose-500 opacity-60" />
              ) : (
                <Shield className="h-8 w-8 lg:h-12 lg:w-12 flex-shrink-0 text-emerald-500 opacity-60" />
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Ready to Fly - Green border */}
        <Card className="border-l-4 border-l-sky-500 bg-sky-50/50">
          <CardContent className="p-4 lg:pt-6 lg:p-6">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs lg:text-sm font-medium text-sky-600">
                  {t.dailyOps?.readyToFly || "Ready to Fly"}
                </p>
                <p className="text-3xl lg:text-4xl font-bold text-sky-700">{readyToFly}</p>
                <p className="text-xs mt-0.5 lg:mt-1 truncate text-sky-600 opacity-70">
                  {t.dailyOps?.missionsReady || "missions ready or active"}
                </p>
              </div>
              <Plane className="h-8 w-8 lg:h-12 lg:w-12 flex-shrink-0 text-sky-500 opacity-60" />
            </div>
          </CardContent>
        </Card>
        
        {/* Completed Today - Grey border */}
        <Card className="border-l-4 border-l-slate-400 bg-slate-50/50">
          <CardContent className="p-4 lg:pt-6 lg:p-6">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs lg:text-sm font-medium text-slate-600">
                  {t.lifecycle?.completed || "Completed"}
                </p>
                <p className="text-3xl lg:text-4xl font-bold text-slate-700">{data?.summary?.completed || 0}</p>
                <p className="text-xs mt-0.5 lg:mt-1 truncate text-slate-600 opacity-70">
                  {t.dailyOps?.completedToday || "completed today"}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 lg:h-12 lg:w-12 flex-shrink-0 text-slate-400 opacity-60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Operational Alerts - With prominent red styling */}
      {data?.operational_alerts?.length > 0 && (
        <Card className="border-l-4 border-l-rose-600 border-rose-200 bg-rose-50/70">
          <CardHeader className="pb-2 px-4 lg:px-6">
            <CardTitle className="text-sm lg:text-base flex items-center gap-2 text-[var(--status-critical)]">
              <AlertOctagon className="h-4 w-4 lg:h-5 lg:w-5" />
              {t.dailyOps?.actionRequired || "Action Required"} ({data.operational_alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 px-4 lg:px-6">
            <div className="space-y-2">
              {data.operational_alerts.slice(0, 5).map((alert, idx) => (
                <div 
                  key={`alert-${alert.mission_uuid}-${alert.type}-${idx}`} 
                  className="flex items-center justify-between gap-2 p-2 lg:p-3 rounded-lg border cursor-pointer transition-all duration-200"
                  style={{ 
                    background: 'var(--bg-elevated)',
                    borderColor: 'var(--border-default)'
                  }}
                  onClick={() => navigate(`/missions/${alert.mission_uuid}`)}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--status-critical)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-default)'}
                >
                  <div className="flex items-center gap-2 lg:gap-3 min-w-0 flex-1">
                    <div className={`p-1.5 lg:p-2 rounded-full flex-shrink-0`} style={{
                      background: alert.severity === "error" ? 'rgba(235, 87, 87, 0.2)' : 'rgba(242, 201, 76, 0.2)',
                      color: alert.severity === "error" ? 'var(--status-critical)' : 'var(--status-warning)'
                    }}>
                      {alert.type === "missing_pilot" && <User className="h-3.5 w-3.5 lg:h-4 lg:w-4" />}
                      {alert.type === "missing_aircraft" && <Plane className="h-3.5 w-3.5 lg:h-4 lg:w-4" />}
                      {alert.type === "compliance_issue" && <AlertOctagon className="h-3.5 w-3.5 lg:h-4 lg:w-4" />}
                      {alert.type === "incomplete_checklist" && <ClipboardList className="h-3.5 w-3.5 lg:h-4 lg:w-4" />}
                      {!["missing_pilot", "missing_aircraft", "compliance_issue", "incomplete_checklist"].includes(alert.type) && 
                        <AlertTriangle className="h-3.5 w-3.5 lg:h-4 lg:w-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs lg:text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                        {t.lang === "cs" ? alert.message_cs : alert.message_en}
                      </p>
                      <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                        {alert.mission_id} • {alert.location}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="gap-1 flex-shrink-0 h-7 lg:h-8 px-2 lg:px-3" style={{ color: 'var(--status-critical)' }}>
                    <span className="hidden sm:inline text-xs lg:text-sm">{t.dailyOps?.fix || "Fix"}</span>
                    <ArrowRight className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                  </Button>
                </div>
              ))}
              {data.operational_alerts.length > 5 && (
                <Button
                  variant="link"
                  className="w-full text-sm"
                  style={{ color: 'var(--status-critical)' }}
                  onClick={() => navigate("/missions")}
                >
                  {t.dailyOps?.viewAllIssues || "View all"} ({data.operational_alerts.length - 5} {t.dailyOps?.more || "more"})
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Insurance Warning Card */}
      {insuranceStatus && (!insuranceStatus.has_valid_coverage || insuranceStatus.expiring_policies > 0 || !insuranceStatus.has_sufficient_coverage) && (
        <Card className={`border-l-4 ${!insuranceStatus.has_valid_coverage ? 'border-l-rose-600 border-rose-200 bg-rose-50/70' : 'border-l-amber-500 border-amber-200 bg-amber-50/70'}`}>
          <CardHeader className="pb-2 px-4 lg:px-6">
            <CardTitle className={`text-sm lg:text-base flex items-center gap-2 ${!insuranceStatus.has_valid_coverage ? 'text-[var(--status-critical)]' : 'text-amber-700'}`}>
              <Shield className="h-4 w-4 lg:h-5 lg:w-5" />
              {t.lang === 'cs' ? 'Upozornění na pojištění' : 'Insurance Warning'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 px-4 lg:px-6">
            <div className="space-y-2">
              {!insuranceStatus.has_valid_coverage && (
                <div 
                  className="flex items-center justify-between gap-2 p-2 lg:p-3 rounded-lg border cursor-pointer transition-all duration-200"
                  style={{ background: 'var(--bg-elevated)', borderColor: 'var(--border-default)' }}
                  onClick={() => navigate('/insurance')}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--status-critical)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-default)'}
                >
                  <div className="flex items-center gap-2 lg:gap-3 min-w-0 flex-1">
                    <div className="p-1.5 lg:p-2 rounded-full flex-shrink-0" style={{ background: 'rgba(235, 87, 87, 0.2)', color: 'var(--status-critical)' }}>
                      <XCircle className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs lg:text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {t.lang === 'cs' ? 'Pojištění vypršelo' : 'Insurance has expired'}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {t.lang === 'cs' ? 'Mise nelze schválit bez platného pojištění' : 'Missions cannot be approved without valid insurance'}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="gap-1 flex-shrink-0 h-7 lg:h-8 px-2 lg:px-3" style={{ color: 'var(--status-critical)' }}>
                    <span className="hidden sm:inline text-xs lg:text-sm">{t.lang === 'cs' ? 'Spravovat' : 'Manage'}</span>
                    <ArrowRight className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                  </Button>
                </div>
              )}
              {insuranceStatus.has_valid_coverage && insuranceStatus.expiring_policies > 0 && (
                <div 
                  className="flex items-center justify-between gap-2 p-2 lg:p-3 rounded-lg border cursor-pointer transition-all duration-200"
                  style={{ background: 'var(--bg-elevated)', borderColor: 'var(--border-default)' }}
                  onClick={() => navigate('/insurance')}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--status-warning)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-default)'}
                >
                  <div className="flex items-center gap-2 lg:gap-3 min-w-0 flex-1">
                    <div className="p-1.5 lg:p-2 rounded-full flex-shrink-0" style={{ background: 'rgba(242, 201, 76, 0.2)', color: 'var(--status-warning)' }}>
                      <Clock className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs lg:text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {t.lang === 'cs' ? `Pojištění vyprší brzy (${insuranceStatus.expiring_policies} ${insuranceStatus.expiring_policies === 1 ? 'pojistka' : 'pojistky'})` : `Insurance expiring soon (${insuranceStatus.expiring_policies} ${insuranceStatus.expiring_policies === 1 ? 'policy' : 'policies'})`}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {insuranceStatus.earliest_expiry && (t.lang === 'cs' 
                          ? `Nejbližší expirace: ${new Date(insuranceStatus.earliest_expiry).toLocaleDateString('cs-CZ')}`
                          : `Earliest expiry: ${new Date(insuranceStatus.earliest_expiry).toLocaleDateString('en-US')}`
                        )}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="gap-1 flex-shrink-0 h-7 lg:h-8 px-2 lg:px-3" style={{ color: 'var(--status-warning)' }}>
                    <span className="hidden sm:inline text-xs lg:text-sm">{t.lang === 'cs' ? 'Zobrazit' : 'View'}</span>
                    <ArrowRight className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                  </Button>
                </div>
              )}
              {insuranceStatus.has_valid_coverage && !insuranceStatus.has_sufficient_coverage && (
                <div 
                  className="flex items-center justify-between gap-2 p-2 lg:p-3 rounded-lg border cursor-pointer transition-all duration-200"
                  style={{ background: 'var(--bg-elevated)', borderColor: 'var(--border-default)' }}
                  onClick={() => navigate('/insurance')}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--status-warning)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-default)'}
                >
                  <div className="flex items-center gap-2 lg:gap-3 min-w-0 flex-1">
                    <div className="p-1.5 lg:p-2 rounded-full flex-shrink-0" style={{ background: 'rgba(242, 201, 76, 0.2)', color: 'var(--status-warning)' }}>
                      <AlertTriangle className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs lg:text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {t.lang === 'cs' ? 'Pokrytí pod minimem EU 2019/947' : 'Coverage below EU 2019/947 minimum'}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {t.lang === 'cs' ? 'Minimální požadované krytí: €750,000' : 'Minimum required coverage: €750,000'}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="gap-1 flex-shrink-0 h-7 lg:h-8 px-2 lg:px-3" style={{ color: 'var(--status-warning)' }}>
                    <span className="hidden sm:inline text-xs lg:text-sm">{t.lang === 'cs' ? 'Zobrazit' : 'View'}</span>
                    <ArrowRight className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Authorization Warning Card */}
      {authorizationStatus && (!authorizationStatus.has_valid_authorizations || authorizationStatus.expiring_authorizations > 0) && (
        <Card className={`border-l-4 ${!authorizationStatus.has_valid_authorizations ? 'border-l-rose-600 border-rose-200 bg-rose-50/70' : 'border-l-amber-500 border-amber-200 bg-amber-50/70'}`}>
          <CardHeader className="pb-2 px-4 lg:px-6">
            <CardTitle className={`text-sm lg:text-base flex items-center gap-2 ${!authorizationStatus.has_valid_authorizations ? 'text-[var(--status-critical)]' : 'text-amber-700'}`}>
              <Award className="h-4 w-4 lg:h-5 lg:w-5" />
              {t.lang === 'cs' ? 'Upozornění na autorizace' : 'Authorization Warning'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 px-4 lg:px-6">
            <div className="space-y-2">
              {!authorizationStatus.has_valid_authorizations && (
                <div 
                  className="flex items-center justify-between gap-2 p-2 lg:p-3 rounded-lg border cursor-pointer transition-all duration-200"
                  style={{ background: 'var(--bg-elevated)', borderColor: 'var(--border-default)' }}
                  onClick={() => navigate('/authorizations')}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--status-critical)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-default)'}
                >
                  <div className="flex items-center gap-2 lg:gap-3 min-w-0 flex-1">
                    <div className="p-1.5 lg:p-2 rounded-full flex-shrink-0" style={{ background: 'rgba(235, 87, 87, 0.2)', color: 'var(--status-critical)' }}>
                      <XCircle className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs lg:text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {t.lang === 'cs' ? 'Žádné platné operační autorizace' : 'No valid operational authorizations'}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {t.lang === 'cs' ? 'Mise SAIL III+ nelze schválit bez platné autorizace' : 'SAIL III+ missions cannot be approved without valid authorization'}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="gap-1 flex-shrink-0 h-7 lg:h-8 px-2 lg:px-3" style={{ color: 'var(--status-critical)' }}>
                    <span className="hidden sm:inline text-xs lg:text-sm">{t.lang === 'cs' ? 'Spravovat' : 'Manage'}</span>
                    <ArrowRight className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                  </Button>
                </div>
              )}
              {authorizationStatus.has_valid_authorizations && authorizationStatus.expiring_authorizations > 0 && (
                <div 
                  className="flex items-center justify-between gap-2 p-2 lg:p-3 rounded-lg border cursor-pointer transition-all duration-200"
                  style={{ background: 'var(--bg-elevated)', borderColor: 'var(--border-default)' }}
                  onClick={() => navigate('/authorizations')}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--status-warning)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-default)'}
                >
                  <div className="flex items-center gap-2 lg:gap-3 min-w-0 flex-1">
                    <div className="p-1.5 lg:p-2 rounded-full flex-shrink-0" style={{ background: 'rgba(242, 201, 76, 0.2)', color: 'var(--status-warning)' }}>
                      <Clock className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs lg:text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {t.lang === 'cs' 
                          ? `Autorizace vyprší brzy (${authorizationStatus.expiring_authorizations})` 
                          : `Authorization expiring soon (${authorizationStatus.expiring_authorizations})`}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {authorizationStatus.earliest_expiry && (t.lang === 'cs' 
                          ? `Nejbližší expirace: ${new Date(authorizationStatus.earliest_expiry).toLocaleDateString('cs-CZ')}`
                          : `Earliest expiry: ${new Date(authorizationStatus.earliest_expiry).toLocaleDateString('en-US')}`
                        )}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="gap-1 flex-shrink-0 h-7 lg:h-8 px-2 lg:px-3" style={{ color: 'var(--status-warning)' }}>
                    <span className="hidden sm:inline text-xs lg:text-sm">{t.lang === 'cs' ? 'Zobrazit' : 'View'}</span>
                    <ArrowRight className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                  </Button>
                </div>
              )}
              {authorizationStatus.covered_sail_levels?.length > 0 && (
                <div className="mt-2 pt-2 border-t" style={{ borderColor: 'var(--border-default)' }}>
                  <p className="text-xs mb-1.5" style={{ color: 'var(--text-muted)' }}>
                    {t.lang === 'cs' ? 'Pokryté úrovně SAIL:' : 'Covered SAIL levels:'}
                  </p>
                  <div className="flex gap-1 flex-wrap">
                    {authorizationStatus.covered_sail_levels.map(level => (
                      <Badge key={level} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                        SAIL {level}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scenario Authorization Warning Card */}
      {scenarioStats && (scenarioStats.expired?.length > 0 || scenarioStats.expiring_soon?.length > 0) && (
        <Card className={`border-l-4 ${scenarioStats.expired?.length > 0 ? 'border-l-rose-600 border-rose-200 bg-rose-50/70' : 'border-l-amber-500 border-amber-200 bg-amber-50/70'}`}>
          <CardHeader className="pb-2 px-4 lg:px-6">
            <CardTitle className={`text-sm lg:text-base flex items-center gap-2 ${scenarioStats.expired?.length > 0 ? 'text-[var(--status-critical)]' : 'text-amber-700'}`}>
              <Target className="h-4 w-4 lg:h-5 lg:w-5" />
              {t.lang === 'cs' ? 'Upozornění na scénáře STS/PDRA' : 'STS/PDRA Scenario Warnings'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 px-4 lg:px-6">
            <div className="space-y-2">
              {scenarioStats.expired?.length > 0 && (
                <div 
                  className="flex items-center justify-between gap-2 p-2 lg:p-3 rounded-lg border cursor-pointer transition-all duration-200"
                  style={{ background: 'var(--bg-elevated)', borderColor: 'var(--border-default)' }}
                  onClick={() => navigate('/scenarios')}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--status-critical)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-default)'}
                >
                  <div className="flex items-center gap-2 lg:gap-3 min-w-0 flex-1">
                    <div className="p-1.5 lg:p-2 rounded-full flex-shrink-0" style={{ background: 'rgba(235, 87, 87, 0.2)', color: 'var(--status-critical)' }}>
                      <XCircle className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs lg:text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {t.lang === 'cs' 
                          ? `${scenarioStats.expired.length} scénář${scenarioStats.expired.length === 1 ? '' : scenarioStats.expired.length < 5 ? 'e' : 'ů'} s prošlou autorizací` 
                          : `${scenarioStats.expired.length} scenario${scenarioStats.expired.length === 1 ? '' : 's'} with expired authorization`}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {scenarioStats.expired.slice(0, 2).map(s => `${s.scenario_name} (${s.scenario_type})`).join(', ')}
                        {scenarioStats.expired.length > 2 && ` +${scenarioStats.expired.length - 2}`}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="gap-1 flex-shrink-0 h-7 lg:h-8 px-2 lg:px-3" style={{ color: 'var(--status-critical)' }}>
                    <span className="hidden sm:inline text-xs lg:text-sm">{t.lang === 'cs' ? 'Obnovit' : 'Renew'}</span>
                    <ArrowRight className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                  </Button>
                </div>
              )}
              {scenarioStats.expiring_soon?.length > 0 && (
                <div 
                  className="flex items-center justify-between gap-2 p-2 lg:p-3 rounded-lg border cursor-pointer transition-all duration-200"
                  style={{ background: 'var(--bg-elevated)', borderColor: 'var(--border-default)' }}
                  onClick={() => navigate('/scenarios')}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--status-warning)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-default)'}
                >
                  <div className="flex items-center gap-2 lg:gap-3 min-w-0 flex-1">
                    <div className="p-1.5 lg:p-2 rounded-full flex-shrink-0" style={{ background: 'rgba(242, 201, 76, 0.2)', color: 'var(--status-warning)' }}>
                      <Clock className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs lg:text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {t.lang === 'cs' 
                          ? `${scenarioStats.expiring_soon.length} scénář${scenarioStats.expiring_soon.length === 1 ? '' : scenarioStats.expiring_soon.length < 5 ? 'e' : 'ů'} brzy expiruje`
                          : `${scenarioStats.expiring_soon.length} scenario${scenarioStats.expiring_soon.length === 1 ? '' : 's'} expiring soon`}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {scenarioStats.expiring_soon.slice(0, 2).map(s => `${s.scenario_name} (${s.days_left}d)`).join(', ')}
                        {scenarioStats.expiring_soon.length > 2 && ` +${scenarioStats.expiring_soon.length - 2}`}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="gap-1 flex-shrink-0 h-7 lg:h-8 px-2 lg:px-3" style={{ color: 'var(--status-warning)' }}>
                    <span className="hidden sm:inline text-xs lg:text-sm">{t.lang === 'cs' ? 'Zobrazit' : 'View'}</span>
                    <ArrowRight className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6">
        {/* Active & Ready Missions */}
        <div className="space-y-3 lg:space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base lg:text-lg font-semibold flex items-center gap-1.5 lg:gap-2" style={{ color: 'var(--text-primary)' }}>
              <PlayCircle className="h-4 w-4 lg:h-5 lg:w-5" style={{ color: 'var(--status-active)' }} />
              {t.dailyOps?.activeAndReady || "Active & Ready"}
            </h2>
            <Badge variant="active" className="text-xs">
              {(data?.missions_by_status?.active?.length || 0) + (data?.missions_by_status?.ready?.length || 0)}
            </Badge>
          </div>
          
          <Card>
            <CardContent className="p-0">
              {/* Active Missions */}
              {data?.missions_by_status?.active?.length > 0 && (
                <>
                  <div className="px-3 lg:px-4 py-1.5 lg:py-2 border-b" style={{ background: 'rgba(45, 156, 219, 0.1)', borderColor: 'rgba(45, 156, 219, 0.2)' }}>
                    <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--status-active)' }}>
                      {t.lifecycle?.active || "Active"} • {t.dailyOps?.inProgress || "In Progress"}
                    </span>
                  </div>
                  {data.missions_by_status.active.map((mission) => (
                    <MissionRow 
                      key={mission.id} 
                      mission={mission} 
                      pilots={pilots}
                      aircraft={aircraft}
                      navigate={navigate}
                      t={t}
                      status="active"
                    />
                  ))}
                </>
              )}
              
              {/* Ready Missions */}
              {data?.missions_by_status?.ready?.length > 0 && (
                <>
                  <div className="px-3 lg:px-4 py-1.5 lg:py-2 border-b border-t" style={{ background: 'rgba(39, 174, 96, 0.1)', borderColor: 'rgba(39, 174, 96, 0.2)' }}>
                    <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--status-success)' }}>
                      {t.lifecycle?.ready || "Ready"} • {t.dailyOps?.readyToStart || "Ready to Start"}
                    </span>
                  </div>
                  {data.missions_by_status.ready.map((mission) => (
                    <MissionRow 
                      key={mission.id} 
                      mission={mission} 
                      pilots={pilots}
                      aircraft={aircraft}
                      navigate={navigate}
                      t={t}
                      status="ready"
                    />
                  ))}
                </>
              )}
              
              {/* No active or ready missions */}
              {(!data?.missions_by_status?.active?.length && !data?.missions_by_status?.ready?.length) && (
                <div className="py-8 lg:py-12 text-center">
                  <Plane className="h-8 w-8 lg:h-10 lg:w-10 mx-auto mb-2 lg:mb-3" style={{ color: 'var(--text-muted)' }} />
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                    {t.dailyOps?.noReadyMissions || "No missions ready to fly"}
                  </p>
                  <Button
                    variant="link"
                    className="mt-1 lg:mt-2 text-sm"
                    onClick={() => navigate("/missions")}
                  >
                    {t.dailyOps?.prepareMission || "Prepare a mission"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Planning & Completed */}
        <div className="space-y-3 lg:space-y-4">
          {/* Draft/Planning Missions */}
          <div className="flex items-center justify-between">
            <h2 className="text-base lg:text-lg font-semibold flex items-center gap-1.5 lg:gap-2" style={{ color: 'var(--text-primary)' }}>
              <Clock className="h-4 w-4 lg:h-5 lg:w-5" style={{ color: 'var(--text-muted)' }} />
              {t.lifecycle?.planning || "Planning"}
            </h2>
            <Badge variant="secondary" className="text-xs">
              {data?.missions_by_status?.draft?.length || 0}
            </Badge>
          </div>
          
          <Card>
            <CardContent className="p-0">
              {data?.missions_by_status?.draft?.length > 0 ? (
                <>
                  {data.missions_by_status.draft.map((mission) => (
                    <MissionRow 
                      key={mission.id} 
                      mission={mission} 
                      pilots={pilots}
                      aircraft={aircraft}
                      navigate={navigate}
                      t={t}
                      status="draft"
                      showBlockers={true}
                    />
                  ))}
                </>
              ) : (
                <div className="py-8 text-center">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2" style={{ color: 'var(--status-success)', opacity: 0.5 }} />
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                    {t.dailyOps?.noDrafts || "No missions in planning"}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Completed Today */}
          {data?.missions_by_status?.completed?.length > 0 && (
            <>
              <div className="flex items-center justify-between mt-6">
                <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <CheckCircle2 className="h-5 w-5" style={{ color: 'var(--brand-accent)' }} />
                  {t.lifecycle?.completed || "Completed"}
                </h2>
                <Badge variant="default" className="text-xs">
                  {data.missions_by_status.completed.length}
                </Badge>
              </div>
              
              <Card>
                <CardContent className="p-0">
                  {data.missions_by_status.completed.slice(0, 3).map((mission) => (
                    <MissionRow 
                      key={mission.id} 
                      mission={mission} 
                      pilots={pilots}
                      aircraft={aircraft}
                      navigate={navigate}
                      t={t}
                      status="completed"
                    />
                  ))}
                  {data.missions_by_status.completed.length > 3 && (
                    <div className="p-3 border-t text-center" style={{ borderColor: 'var(--border-default)' }}>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => navigate("/missions/history")}
                      >
                        {t.dailyOps?.viewHistory || "View History"} ({data.missions_by_status.completed.length - 3} {t.dailyOps?.more || "more"})
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      {/* No missions today */}
      {data?.total_missions === 0 && (
        <Card>
          <CardContent className="py-16 text-center">
            <Calendar className="h-16 w-16 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              {t.dailyOps?.noMissionsToday || "No missions scheduled for today"}
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
              {t.dailyOps?.scheduleHint || "Schedule a mission to get started"}
            </p>
            <Button onClick={() => navigate("/missions")} className="gap-2">
              <FileText className="h-4 w-4" />
              {t.dailyOps?.viewAllMissions || "View All Missions"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions Footer */}
      <div className="flex items-center justify-center gap-4 pt-4 border-t" style={{ borderColor: 'var(--border-default)' }}>
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => navigate("/missions")}
        >
          <FileText className="h-4 w-4" />
          {t.dailyOps?.viewAllMissions || "All Missions"}
        </Button>
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => navigate("/missions/history")}
        >
          <Clock className="h-4 w-4" />
          {t.dailyOps?.viewHistory || "Mission History"}
        </Button>
      </div>
    </div>
  );
};

// Mission Row Component - Theme-Aware
const MissionRow = ({ mission, pilots, aircraft, navigate, t, status, showBlockers = false }) => {
  const pilot = pilots[mission.pilot_id];
  const ac = aircraft[mission.aircraft_id];
  
  // Determine blockers
  const blockers = [];
  if (!mission.pilot_id) blockers.push(t.dailyOps?.noPilot || "No pilot");
  if (!mission.aircraft_id) blockers.push(t.dailyOps?.noAircraft || "No aircraft");
  if (!mission.compliance_flag) blockers.push(t.missionStatus?.nonCompliant || "Non-compliant");
  
  const getStatusStyle = () => {
    switch(status) {
      case 'active': return { borderLeft: '4px solid var(--status-active)', background: 'rgba(45, 156, 219, 0.05)' };
      case 'ready': return { borderLeft: '4px solid var(--status-success)', background: 'rgba(39, 174, 96, 0.05)' };
      case 'completed': return { borderLeft: '4px solid var(--brand-accent)', background: 'rgba(47, 128, 237, 0.05)' };
      case 'draft': 
        return blockers.length > 0 
          ? { borderLeft: '4px solid var(--status-warning)', background: 'rgba(242, 201, 76, 0.05)' }
          : { borderLeft: '4px solid var(--text-muted)' };
      default: return {};
    }
  };
  
  return (
    <div 
      className="p-3 lg:p-4 border-b last:border-b-0 cursor-pointer transition-all duration-200"
      style={{ 
        ...getStatusStyle(),
        borderBottomColor: 'var(--border-default)'
      }}
      onClick={() => navigate(`/missions/${mission.id}`)}
      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-elevated)'}
      onMouseLeave={(e) => e.currentTarget.style.background = getStatusStyle().background || 'transparent'}
      data-testid={`mission-row-${mission.id}`}
    >
      <div className="flex items-start justify-between gap-2 lg:gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 lg:gap-2">
            <p className="font-semibold text-sm lg:text-base" style={{ color: 'var(--text-primary)' }}>{mission.mission_id}</p>
            {status === "active" && (
              <Badge variant="active" className="text-xs">
                <PlayCircle className="h-3 w-3 mr-0.5" />
                {t.lifecycle?.active || "Active"}
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 lg:gap-x-4 gap-y-0.5 text-xs lg:text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
            <span className="flex items-center gap-1 truncate max-w-[150px] lg:max-w-none">
              <MapPin className="h-3 w-3 lg:h-3.5 lg:w-3.5 flex-shrink-0" /> 
              <span className="truncate">{mission.location}</span>
            </span>
            <span className="flex items-center gap-1 truncate">
              <User className="h-3 w-3 lg:h-3.5 lg:w-3.5 flex-shrink-0" /> 
              {pilot?.name || <span style={{ color: 'var(--status-warning)' }} className="italic">{t.dailyOps?.unassigned || "Unassigned"}</span>}
            </span>
            <span className="flex items-center gap-1 truncate hidden sm:flex">
              <Plane className="h-3 w-3 lg:h-3.5 lg:w-3.5 flex-shrink-0" /> 
              {ac?.model || <span style={{ color: 'var(--status-warning)' }} className="italic">{t.dailyOps?.unassigned || "Unassigned"}</span>}
            </span>
          </div>
          
          {/* Show blockers for draft missions */}
          {showBlockers && blockers.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 lg:gap-2 mt-1.5 lg:mt-2">
              <span className="text-xs font-medium" style={{ color: 'var(--status-warning)' }}>
                {t.dailyOps?.blockers || "Blocking"}:
              </span>
              {blockers.map((blocker, idx) => (
                <Badge key={`blocker-${blocker.substring(0, 10).replace(/\s+/g, '-')}-${idx}`} variant="warning" className="text-xs py-0">
                  {blocker}
                </Badge>
              ))}
            </div>
          )}
        </div>
        
        <ChevronRight className="h-4 w-4 lg:h-5 lg:w-5 flex-shrink-0 mt-0.5 lg:mt-1" style={{ color: 'var(--text-muted)' }} />
      </div>
    </div>
  );
};

export default DailyOpsDashboard;
